// src/pages/legal/PrivacyPolicy.tsx

import React from "react";
import { Link } from "react-router-dom";

import "./LegalPages.css";

// =====================================================
// PRIVACY POLICY
// =====================================================

const PrivacyPolicy: React.FC = () => {
  return (
    <main className="legal-page">

      {/* ================================================= */}
      {/* HERO                                              */}
      {/* ================================================= */}

      <section className="legal-hero">

        <div className="legal-hero-glow" />

        <div className="legal-hero-content">

          {/* ============================================= */}
          {/* BREADCRUMB                                    */}
          {/* ============================================= */}

          <nav
            className="legal-breadcrumb"
            aria-label="Breadcrumb"
          >
            <Link to="/">
              P-ARENA
            </Link>

            <span>/</span>

            <span>
              Legal
            </span>

            <span>/</span>

            <strong aria-current="page">
              Privacy Policy
            </strong>
          </nav>

          {/* ============================================= */}
          {/* EMBLEM                                        */}
          {/* ============================================= */}

          <div className="legal-hero-emblem">
            ♛
          </div>

          <p className="legal-eyebrow">
            P-ARENA LEGAL
          </p>

          <h1>
            Privacy Policy
          </h1>

          <div className="legal-divider">
            <span />
            <strong>◆</strong>
            <span />
          </div>

          <p className="legal-hero-description">
            Your privacy matters to us. This Privacy
            Policy explains what information P-ARENA
            collects, how we use it, and the choices
            available to you.
          </p>

          <div className="legal-last-updated">
            Last Updated: August 2026
          </div>

        </div>

      </section>

      {/* ================================================= */}
      {/* PAGE NAVIGATION                                   */}
      {/* ================================================= */}

      <nav
        className="legal-section-nav"
        aria-label="Privacy Policy sections"
      >
        <div className="legal-section-nav-inner">

          <a href="#introduction">
            Introduction
          </a>

          <a href="#information">
            Information We Collect
          </a>

          <a href="#use">
            How We Use Information
          </a>

          <a href="#sharing">
            Information Sharing
          </a>

          <a href="#cookies">
            Cookies
          </a>

          <a href="#security">
            Security
          </a>

          <a href="#retention">
            Data Retention
          </a>

          <a href="#rights">
            Your Rights
          </a>

          <a href="#children">
            Children
          </a>

          <a href="#changes">
            Changes
          </a>

          <a href="#contact">
            Contact
          </a>

        </div>
      </nav>

      {/* ================================================= */}
      {/* CONTENT                                           */}
      {/* ================================================= */}

      <div className="legal-content">

        {/* ================================================= */}
        {/* INTRODUCTION                                      */}
        {/* ================================================= */}

        <section
          id="introduction"
          className="legal-section"
        >

          <div className="legal-section-header">

            <span className="legal-section-number">
              01
            </span>

            <div>
              <p>
                OVERVIEW
              </p>

              <h2>
                Introduction
              </h2>
            </div>

          </div>

          <div className="legal-copy">

            <p>
              Welcome to <strong>P-ARENA</strong>.
              This Privacy Policy explains how
              P-ARENA collects, uses, stores, and
              protects information when you access
              or use our website, games, account
              features, multiplayer services, and
              related services.
            </p>

            <p>
              This Privacy Policy applies to
              information collected through P-ARENA
              and the services made available through
              the platform.
            </p>

            <p>
              By using P-ARENA, you acknowledge the
              practices described in this Privacy
              Policy.
            </p>

          </div>

        </section>

        {/* ================================================= */}
        {/* INFORMATION WE COLLECT                            */}
        {/* ================================================= */}

        <section
          id="information"
          className="legal-section"
        >

          <div className="legal-section-header">

            <span className="legal-section-number">
              02
            </span>

            <div>
              <p>
                YOUR INFORMATION
              </p>

              <h2>
                Information We Collect
              </h2>
            </div>

          </div>

          <div className="legal-copy">

            <p>
              The information we collect depends on
              how you interact with P-ARENA and which
              features you use.
            </p>

          </div>

          <div className="legal-card-grid">

            {/* ACCOUNT INFORMATION */}

            <article className="legal-info-card">

              <span className="legal-card-number">
                01
              </span>

              <h3>
                Account Information
              </h3>

              <p>
                When you create or maintain a
                P-ARENA account, we may collect
                information such as:
              </p>

              <ul>
                <li>
                  Full name
                </li>

                <li>
                  Username
                </li>

                <li>
                  Email address
                </li>

                <li>
                  Account identifiers
                </li>

                <li>
                  Authentication information
                </li>
              </ul>

            </article>

            {/* PROFILE INFORMATION */}

            <article className="legal-info-card">

              <span className="legal-card-number">
                02
              </span>

              <h3>
                Profile Information
              </h3>

              <p>
                If you use player profile features,
                we may store information associated
                with your profile, including:
              </p>

              <ul>
                <li>
                  Profile avatar
                </li>

                <li>
                  Biography or profile description
                </li>

                <li>
                  Player rating
                </li>

                <li>
                  Account creation date
                </li>
              </ul>

            </article>

            {/* GAME INFORMATION */}

            <article className="legal-info-card">

              <span className="legal-card-number">
                03
              </span>

              <h3>
                Game Information
              </h3>

              <p>
                When you play games on P-ARENA,
                information about gameplay may be
                recorded, including:
              </p>

              <ul>
                <li>
                  Games played
                </li>

                <li>
                  Wins, losses, and draws
                </li>

                <li>
                  Game scores
                </li>

                <li>
                  Highest scores
                </li>

                <li>
                  Match history
                </li>

                <li>
                  Opponent type
                </li>

                <li>
                  Player rating
                </li>
              </ul>

            </article>

            {/* TECHNICAL INFORMATION */}

            <article className="legal-info-card">

              <span className="legal-card-number">
                04
              </span>

              <h3>
                Technical Information
              </h3>

              <p>
                When you access P-ARENA, certain
                technical information may be
                collected automatically when needed
                to operate, protect, or improve the
                service.
              </p>

              <ul>
                <li>
                  Browser or device information
                </li>

                <li>
                  IP address
                </li>

                <li>
                  Login and session information
                </li>

                <li>
                  Error and diagnostic information
                </li>

                <li>
                  Security-related activity
                </li>
              </ul>

            </article>

          </div>

        </section>

        {/* ================================================= */}
        {/* HOW INFORMATION IS USED                           */}
        {/* ================================================= */}

        <section
          id="use"
          className="legal-section"
        >

          <div className="legal-section-header">

            <span className="legal-section-number">
              03
            </span>

            <div>
              <p>
                PURPOSE
              </p>

              <h2>
                How We Use Your Information
              </h2>
            </div>

          </div>

          <div className="legal-copy">

            <p>
              We may use information collected
              through P-ARENA to operate and improve
              the platform, including to:
            </p>

          </div>

          <div className="legal-purpose-list">

            <article>
              <span>
                01
              </span>

              <div>
                <h3>
                  Provide Your Account
                </h3>

                <p>
                  Create, authenticate, maintain,
                  and manage your P-ARENA account
                  and player profile.
                </p>
              </div>
            </article>

            <article>
              <span>
                02
              </span>

              <div>
                <h3>
                  Operate Games
                </h3>

                <p>
                  Run game sessions, matchmaking,
                  AI games, multiplayer games,
                  scoring, rankings, and other game
                  functionality.
                </p>
              </div>
            </article>

            <article>
              <span>
                03
              </span>

              <div>
                <h3>
                  Maintain Player Statistics
                </h3>

                <p>
                  Record game history, scores,
                  wins, losses, draws, ratings, and
                  other player statistics.
                </p>
              </div>
            </article>

            <article>
              <span>
                04
              </span>

              <div>
                <h3>
                  Protect P-ARENA
                </h3>

                <p>
                  Detect abuse, unauthorized access,
                  cheating, fraud, security threats,
                  or activity that may violate our
                  Terms &amp; Conditions.
                </p>
              </div>
            </article>

            <article>
              <span>
                05
              </span>

              <div>
                <h3>
                  Improve the Platform
                </h3>

                <p>
                  Diagnose technical issues,
                  understand platform performance,
                  and develop or improve P-ARENA
                  features.
                </p>
              </div>
            </article>

          </div>

        </section>

        {/* ================================================= */}
        {/* INFORMATION SHARING                               */}
        {/* ================================================= */}

        <section
          id="sharing"
          className="legal-section"
        >

          <div className="legal-section-header">

            <span className="legal-section-number">
              04
            </span>

            <div>
              <p>
                DISCLOSURE
              </p>

              <h2>
                How We Share Information
              </h2>
            </div>

          </div>

          <div className="legal-highlight">

            <span className="legal-highlight-icon">
              ♛
            </span>

            <div>
              <small>
                OUR PRINCIPLE
              </small>

              <h3>
                Your Personal Information Is Not
                Sold
              </h3>

              <p>
                P-ARENA does not sell your personal
                information for money.
              </p>
            </div>

          </div>

          <div className="legal-copy">

            <p>
              We may disclose information when
              reasonably necessary to operate the
              platform, including to service
              providers that support hosting,
              infrastructure, security, email,
              storage, or other technical services.
            </p>

            <p>
              We may also disclose information when
              required by applicable law, legal
              process, court order, or governmental
              request, or when reasonably necessary
              to protect the rights, safety, and
              security of P-ARENA, our users, or
              others.
            </p>

            <p>
              If P-ARENA is involved in a merger,
              acquisition, financing,
              reorganization, sale of assets, or
              similar business transaction,
              information may be transferred as
              part of that transaction, subject to
              applicable law.
            </p>

          </div>

        </section>

        {/* ================================================= */}
        {/* COOKIES                                           */}
        {/* ================================================= */}

        <section
          id="cookies"
          className="legal-section"
        >

          <div className="legal-section-header">

            <span className="legal-section-number">
              05
            </span>

            <div>
              <p>
                BROWSER TECHNOLOGIES
              </p>

              <h2>
                Cookies &amp; Local Storage
              </h2>
            </div>

          </div>

          <div className="legal-copy">

            <p>
              P-ARENA may use cookies, browser
              storage, session storage, or similar
              technologies where necessary to
              provide and secure platform
              functionality.
            </p>

            <p>
              These technologies may be used for
              purposes such as maintaining login
              sessions, remembering preferences,
              supporting security features, or
              maintaining application state.
            </p>

            <p>
              For more information, please review
              our{" "}
              <Link
                to="/cookie-policy"
                className="legal-inline-link"
              >
                Cookie Policy
              </Link>.
            </p>

          </div>

        </section>

        {/* ================================================= */}
        {/* DATA SECURITY                                     */}
        {/* ================================================= */}

        <section
          id="security"
          className="legal-section"
        >

          <div className="legal-section-header">

            <span className="legal-section-number">
              06
            </span>

            <div>
              <p>
                PROTECTION
              </p>

              <h2>
                Data Security
              </h2>
            </div>

          </div>

          <div className="legal-copy">

            <p>
              We use reasonable administrative,
              technical, and organizational
              safeguards designed to protect
              information from unauthorized access,
              loss, misuse, alteration, or
              disclosure.
            </p>

            <p>
              However, no internet transmission,
              network, database, or electronic
              storage system can be guaranteed to
              be completely secure.
            </p>

            <p>
              You are responsible for maintaining
              the confidentiality of your account
              credentials and for promptly notifying
              us if you believe your account has
              been accessed without authorization.
            </p>

          </div>

        </section>

        {/* ================================================= */}
        {/* DATA RETENTION                                    */}
        {/* ================================================= */}

        <section
          id="retention"
          className="legal-section"
        >

          <div className="legal-section-header">

            <span className="legal-section-number">
              07
            </span>

            <div>
              <p>
                STORAGE
              </p>

              <h2>
                Data Retention
              </h2>
            </div>

          </div>

          <div className="legal-copy">

            <p>
              We may retain personal information for
              as long as reasonably necessary to
              provide P-ARENA, maintain accounts,
              preserve game records, protect the
              platform, comply with legal
              obligations, resolve disputes, and
              enforce agreements.
            </p>

            <p>
              Different categories of information
              may be retained for different periods
              depending on their purpose and
              applicable legal requirements.
            </p>

          </div>

        </section>

        {/* ================================================= */}
        {/* USER RIGHTS                                       */}
        {/* ================================================= */}

        <section
          id="rights"
          className="legal-section"
        >

          <div className="legal-section-header">

            <span className="legal-section-number">
              08
            </span>

            <div>
              <p>
                YOUR CHOICES
              </p>

              <h2>
                Your Privacy Rights
              </h2>
            </div>

          </div>

          <div className="legal-copy">

            <p>
              Depending on where you live,
              applicable privacy law may provide
              rights regarding your personal
              information.
            </p>

          </div>

          <div className="legal-rights-grid">

            <article>
              <strong>
                01
              </strong>

              <h3>
                Access
              </h3>

              <p>
                You may have the right to request
                access to personal information we
                maintain about you.
              </p>
            </article>

            <article>
              <strong>
                02
              </strong>

              <h3>
                Correction
              </h3>

              <p>
                You may have the right to request
                correction of inaccurate personal
                information.
              </p>
            </article>

            <article>
              <strong>
                03
              </strong>

              <h3>
                Deletion
              </h3>

              <p>
                You may have the right to request
                deletion of certain personal
                information, subject to applicable
                exceptions.
              </p>
            </article>

            <article>
              <strong>
                04
              </strong>

              <h3>
                Additional Rights
              </h3>

              <p>
                Depending on applicable law, you
                may have additional rights regarding
                the processing or disclosure of your
                personal information.
              </p>
            </article>

          </div>

          <div className="legal-note">

            <strong>
              ◆
            </strong>

            <p>
              We may need to verify your identity
              before processing certain privacy
              requests. Some information may be
              retained where permitted or required
              by law.
            </p>

          </div>

        </section>

        {/* ================================================= */}
        {/* CHILDREN                                          */}
        {/* ================================================= */}

        <section
          id="children"
          className="legal-section"
        >

          <div className="legal-section-header">

            <span className="legal-section-number">
              09
            </span>

            <div>
              <p>
                AGE &amp; PRIVACY
              </p>

              <h2>
                Children's Privacy
              </h2>
            </div>

          </div>

          <div className="legal-copy">

            <p>
              P-ARENA is not intended to knowingly
              collect personal information from
              children in violation of applicable
              law.
            </p>

            <p>
              If you believe a child has provided
              personal information to P-ARENA in a
              manner that is not permitted by law,
              please contact us so that we can
              review the matter and take appropriate
              action.
            </p>

          </div>

        </section>

        {/* ================================================= */}
        {/* THIRD-PARTY SERVICES                              */}
        {/* ================================================= */}

        <section className="legal-section">

          <div className="legal-section-header">

            <span className="legal-section-number">
              10
            </span>

            <div>
              <p>
                EXTERNAL SERVICES
              </p>

              <h2>
                Third-Party Services
              </h2>
            </div>

          </div>

          <div className="legal-copy">

            <p>
              P-ARENA may rely on third-party
              technology or service providers to
              operate certain parts of the
              platform.
            </p>

            <p>
              Third-party websites or services may
              have their own privacy practices.
              P-ARENA is not responsible for the
              privacy practices of third-party
              websites or services that are not
              controlled by P-ARENA.
            </p>

          </div>

        </section>

        {/* ================================================= */}
        {/* POLICY CHANGES                                    */}
        {/* ================================================= */}

        <section
          id="changes"
          className="legal-section"
        >

          <div className="legal-section-header">

            <span className="legal-section-number">
              11
            </span>

            <div>
              <p>
                UPDATES
              </p>

              <h2>
                Changes to This Privacy Policy
              </h2>
            </div>

          </div>

          <div className="legal-copy">

            <p>
              We may update this Privacy Policy from
              time to time to reflect changes to
              P-ARENA, our practices, technology,
              or applicable legal requirements.
            </p>

            <p>
              When this Privacy Policy is updated,
              the "Last Updated" date displayed at
              the top of this page will be revised.
            </p>

            <p>
              We encourage you to review this page
              periodically for updates.
            </p>

          </div>

        </section>

        {/* ================================================= */}
        {/* CONTACT                                           */}
        {/* ================================================= */}

        <section
          id="contact"
          className="legal-section"
        >

          <div className="legal-section-header">

            <span className="legal-section-number">
              12
            </span>

            <div>
              <p>
                QUESTIONS
              </p>

              <h2>
                Contact Us
              </h2>
            </div>

          </div>

          <div className="legal-contact-card">

            <div className="legal-contact-emblem">
              ♠ ♥ ♦ ♣
            </div>

            <small>
              P-ARENA
            </small>

            <h3>
              Privacy Questions
            </h3>

            <p>
              If you have questions about this
              Privacy Policy or would like to make
              a privacy-related request, please
              contact P-ARENA through the contact
              information provided on the platform.
            </p>

          </div>

        </section>

        {/* ================================================= */}
        {/* LEGAL NAVIGATION                                  */}
        {/* ================================================= */}

        <section className="legal-footer-navigation">

          <div className="legal-footer-suits">
            <span>♠</span>
            <span>♥</span>
            <span>♦</span>
            <span>♣</span>
          </div>

          <small>
            P-ARENA
          </small>

          <h2>
            Legal Information
          </h2>

          <p>
            Review the policies governing your use
            of P-ARENA.
          </p>

          <div className="legal-footer-links">

            <Link
              to="/privacy-policy"
              className="active"
            >
              Privacy Policy
            </Link>

            <Link to="/terms-conditions">
              Terms &amp; Conditions
            </Link>

            <Link to="/cookie-policy">
              Cookie Policy
            </Link>

          </div>

          <Link
            to="/"
            className="legal-back-home"
          >
            ← Back to Game House
          </Link>

        </section>

      </div>

    </main>
  );
};

export default PrivacyPolicy;